import React, {
  useCallback,
  useState,
} from 'react';
import './StartupPage.scss';
import { useStores } from '../../hooks/use-stores';
import { NETWORK_PING_INTERVAL_MS } from '../../utils/is-online';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import OfflineModeModal from './OfflineModeModal';
import batchGoLogo from '../../../img/batchgo_blk.svg';

interface StartupPageProps {
  onProceed: () => void;
}

const TIMEOUT_BUFFER = 50;
const OFFLINE_TIMEOUT = NETWORK_PING_INTERVAL_MS + TIMEOUT_BUFFER;

export const StartupPage = ({ onProceed }: StartupPageProps) => {
  const { t } = useTranslation();
  const { newAuthStore, connectionStore, asyncStore } = useStores();
  const [isOfflineLoginDataMissing, setIsOfflineLoginDataMissing] = useState(false);
  const [isOfflineModeLoading, setIsOfflineModeLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleInitSSO = useCallback(() => {
    if (connectionStore.simulateOffline) {
      connectionStore.toggleOffline();
      asyncStore.setTimeout(async () => {
        newAuthStore.init();
        onProceed();
      }, OFFLINE_TIMEOUT);
      return;
    }

    newAuthStore.init();
    onProceed();
  }, [newAuthStore, connectionStore, asyncStore, onProceed]);

  const handleOpenOfflineModeModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleStartOfflineMode = useCallback(() => {
    setIsModalOpen(false);
    setIsOfflineModeLoading(true);
    connectionStore.toggleOffline();
    asyncStore.setTimeout(async () => {
      try {
        // Wait for the network ping to fail (because offline mode is enabled)
        const offlineLoginDataExists = await newAuthStore.doesOfflineLoginDataExist();
        if (offlineLoginDataExists) {
          newAuthStore.init();
          onProceed();
        } else {
          setIsOfflineLoginDataMissing(true);
        }
      } catch (error) {
        logger.error('Error while checking offline login data:', error);
        setIsOfflineLoginDataMissing(true);
      } finally {
        setIsOfflineModeLoading(false);
      }
    }, OFFLINE_TIMEOUT);
  }, [asyncStore, newAuthStore, connectionStore, onProceed]);

  return (
    <div className="startup-page">
      <div className={'startup-page__auth-container'}>
        <img
          className={'logo'}
          src={batchGoLogo}
          alt="BatchGo"
        />
        <div className="startup-card">
          <h1>{t('Welcome Back')}</h1>
          <p className="subtitle">
            {t('Please choose your preferred login method.')}
          </p>
          <div className="button-container">
            <button
              id="btn-continue-with-sso"
              className="button btn-primary"
              onClick={handleInitSSO}
            >
              {t('Continue with SSO')}
            </button>
            <div className={'separator'}>
              <div className={'separator__line'}/>
              <p className={'separator__text'}>or</p>
              <div className={'separator__line'}/>
            </div>
            <button
              className={'link'}
              id="bn-continue-offline"
              onClick={handleOpenOfflineModeModal}
              disabled={isOfflineModeLoading}
            >
              {isOfflineModeLoading ? t('Loading...') : t('Continue Offline')}
            </button>
            {isOfflineLoginDataMissing && (
              <div
                className={'error-container'}
              >
                <p className="error-text">
                  {t('Offline login data is missing. You need to login online at least once to use the offline mode.')}
                </p>
              </div>
            )}
          </div>
          <p className="helper-text">
            {t('Please select the offline mode only if you are having issues with the internet connection OR connecting with our servers.')}
          </p>
        </div>
      </div>
      <OfflineModeModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={handleStartOfflineMode}
      />
    </div>
  );
};

export default StartupPage;
